import { CloseOutlined } from "@ant-design/icons";
import { Alert, message, Modal } from "antd";
import { Button, Checkbox, Input, List, Space } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import PincodeInput from "~/components/form/PincodeInput.jsx";
import { SelectUser } from "~/components/profile/selectUser.jsx";


export const AuthoriseStudent = ({
  aanmeldingen = [],
  visible = false,
  onClose = () => {},
  signAsStudent = false,
}) => {
  const [limitations, setLimitations] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // signAsStudent mode: instructor selected by the student handing over the phone
  const [instructor, setInstructor] = useState(null);
  const [pincode, setPincode] = useState("");

  const unauthorizedAanmeldingen = aanmeldingen.filter((a) => !a.authorised);

  const { club, vlieger_data } = useSelector((state) => state.persist);
  const defaultLimitations = club?.default_limitations || [];
  const dispatch = useDispatch();

  const { vliegers } = useSelector((state) => state.flights);
  const { aanmeldingen: allAanmeldingen } = useSelector((state) => state.aanmeldingen);
  const possibleInstructors = (vliegers || []).filter((v) => v.is_instructeur);

  const signedUpInstructorIds = new Set(
    (allAanmeldingen || []).filter((a) => a.as_instructeur && a.aangemeld).map((a) => a.vlieger?.id)
  );
  const signedUpInstructors = possibleInstructors.filter((v) => signedUpInstructorIds.has(v.id));
  const otherInstructors = possibleInstructors.filter((v) => !signedUpInstructorIds.has(v.id));
  const sortedInstructors = [...signedUpInstructors, ...otherInstructors];

  const fisInvalid = !signAsStudent && vlieger_data?.recency?.fis == 'invalid';

  useEffect(() => {
    if (visible) {
      setLimitations("");
      setSelectedIds(aanmeldingen?.length === 1 ? [aanmeldingen[0].id] : []);
      setInstructor(null);
      setPincode("");
    }
  }, [visible, aanmeldingen]);

  const handleAuthorize = async () => {
    if (selectedIds.length === 0) return;
    if (signAsStudent && (!instructor || !pincode)) return;

    setLoading(true);
    try {
      const payload = {
        aanmelding_ids: selectedIds,
        limitations: limitations,
        ...(signAsStudent && instructor && { instructor_id: instructor.id, pincode }),
      };
      const data = await api.post("aanmeldingen/authorise/", payload);

      if (data === "invalid_pincode") {
        message.error($t("Pincode is onjuist. Probeer opnieuw."));
        setPincode("");
        return;
      }

      if (data) {
        dispatch({
          type: "AANMELDING_SUCCESS",
          normalizeAsVliegers: false,
          default_vliegers: data.default_vliegers,
          dag_id: data.dag_id,
          ...data,
        });

        setLimitations("");
        setSelectedIds([]);
        setInstructor(null);
        setPincode("");
        if (signAsStudent) {
          onClose();
          Modal.success({
            title: $t("Authorisatie voltooid"),
            content: $t("Veel plezier met vliegen"),
          });
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error("Authorization failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (aanmeldingId) => {
    setSelectedIds((prev) =>
      prev.includes(aanmeldingId)
        ? prev.filter((id) => id !== aanmeldingId)
        : [...prev, aanmeldingId],
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === unauthorizedAanmeldingen.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(unauthorizedAanmeldingen.map((a) => a.id));
    }
  };

  const addLimitation = (limitation) => {
    if (!limitations) {
      setLimitations(limitation);
    } else {
      const needsPeriod = !limitations.trim().endsWith(".");
      setLimitations(limitations + (needsPeriod ? ". " : " ") + limitation);
    }
  };

  const submitDisabled =
    selectedIds.length === 0 ||
    fisInvalid ||
    (signAsStudent && (!instructor || !pincode));

  return (
    <Modal
      title={$t("Autoriseer Vliegers")}
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button
          key="authorize"
          type="primary"
          onClick={handleAuthorize}
          loading={loading}
          disabled={submitDisabled}
        >
          {$t("Autoriseer")} ({selectedIds.length})
        </Button>,
        <Button key="cancel" onClick={onClose}>
          {$t("Sluiten")}
        </Button>,
      ]}
    >

      {fisInvalid && (
        <Alert type="error" showIcon message={
          $t("Instructiebevoegheid is ongeldig. Authoriseren kan alleen als je zelf aan FIS-eisen voldoet.")
        }/>
      )}

      <Space direction="vertical" style={{ width: "100%" }} size="middle">

        {/* Pilot list — hidden in signAsStudent mode (single pilot, pre-selected) */}
        {!signAsStudent && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space style={{ justifyContent: "space-between", width: "100%" }}>
              <strong className="label">{$t("Vliegers")}</strong>
              {unauthorizedAanmeldingen.length > 1 && (
                <Checkbox
                  checked={selectedIds.length === unauthorizedAanmeldingen.length}
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < unauthorizedAanmeldingen.length
                  }
                  onChange={toggleSelectAll}
                >
                  {$t("Selecteer alles")}
                </Checkbox>
              )}
            </Space>

            <List
              size="small"
              bordered
              dataSource={unauthorizedAanmeldingen}
              renderItem={(aanmelding) => (
                <List.Item>
                  <Checkbox
                    checked={selectedIds.includes(aanmelding.id)}
                    onChange={() => toggleSelection(aanmelding.id)}
                  >
                    {aanmelding.vlieger?.name}
                  </Checkbox>
                </List.Item>
              )}
            />
          </Space>
        )}

        {/* Instructor selector + pincode — only in signAsStudent mode */}
        {signAsStudent && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <p className="small gray">
              {$t("Geef je telefoon aan een instructeur. De instructeur selecteert zichzelf en vult zijn pincode in.")}
            </p>
            <strong className="label">{$t("Instructeur")}</strong>
            <SelectUser
              users={sortedInstructors}
              value={instructor}
              onChange={setInstructor}
            />
            {signedUpInstructors.length > 0 && (
              <Space wrap size="small">
                {signedUpInstructors.map((v) => (
                  <Button
                    key={v.id}
                    size="small"
                    type={instructor?.id === v.id ? "primary" : "default"}
                    onClick={() => setInstructor(instructor?.id === v.id ? null : v)}
                  >
                    {v.name}
                  </Button>
                ))}
              </Space>
            )}
            <strong className="label">{$t("Pincode")}</strong>
            <PincodeInput
              disabled={!instructor}
              value={pincode}
              onComplete={(v) => v && setPincode(v)}
            />
          </Space>
        )}

        <div>
          <label>
            {$t("Beperkingen")} ({$t("Optioneel")}):
          </label>
          <Input.TextArea
            rows={3}
            value={limitations}
            onChange={(e) => setLimitations(e.target.value)}
            placeholder={$t("Bijv: Max wind 15kts, alleen lokaal, max 2 uur")}
            style={{ marginTop: 8 }}
          />
          <Space wrap size="small" style={{ marginTop: 8 }}>
            {defaultLimitations.map((limitation, index) => {
              const displayText =
                limitation.length > 30
                  ? limitation.substring(0, 27) + "..."
                  : limitation;
              return (
                <Button
                  key={index}
                  size="small"
                  onClick={() => addLimitation(limitation)}
                >
                  + "{displayText}"
                </Button>
              );
            })}
            <Button
              size="small"
              danger
              icon={<CloseOutlined />}
              onClick={() => setLimitations("")}
            >
              {$t("Wissen")}
            </Button>
          </Space>
        </div>
      </Space>
    </Modal>
  );
};
